import { Centered, useLoading } from '@gorila-shared-ui/components';
import { LoadingSpinner } from 'baseui/button/styled-components';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { getDeliveryReports } from '../../../services/reportsService';
import { apiParamsState, isAllWorkspacesSelect } from '../../../storage/app';
import {
  activeDeliveryState,
  delivertReportState,
  deliveryDateState,
  deliveryReportStates,
  updateReportState,
} from '../../../storage/reports';
import { DeliveryReport } from '../../../types/reports';
import DeliveryReportTable from './DeliveryReportTable';

type Props = {
  assetId: string;
};

export function DeliveryReportTableFromAsset({ assetId }: Readonly<Props>) {
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const apiParams = useRecoilValue(apiParamsState);
  const date = useRecoilValue(deliveryDateState);
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);
  const setActiveReport = useSetRecoilState(activeDeliveryState);
  const setDeliveryReports = useSetRecoilState(deliveryReportStates);
  const states = useRecoilValue(delivertReportState(assetId));
  const updateReport = useRecoilValue(updateReportState);

  useUpdateEffect(() => {
    if (!date || !apiParams) return;
    setPage(0);
  }, [date, apiParams?.clientId, apiParams?.subClientId, isAllWorkspacesSelected, assetId]);

  useEffect(() => {
    if (!date || !apiParams) return;
    if (page === 0) {
      setPage(1);
      return;
    }

    const loadDeliveryReports = async () => {
      startLoading();
      const { error, reports, total } = await getDeliveryReports({
        page,
        clientId: apiParams.clientId,
        date: moment(date).format('YYYYMMDD'),
        assetId,
        subClientId: isAllWorkspacesSelected ? undefined : apiParams.subClientId,
      });
      setTotalItems(total || 0);
      const newReports: { [key: string]: DeliveryReport[] } = {};
      if (!error) {
        newReports[assetId] = reports ?? [];
      }
      setDeliveryReports(newReports);

      if (newReports[assetId]) {
        setActiveReport(assetId);
      }
      stopLoading();
    };

    loadDeliveryReports();
  }, [page, updateReport]);

  if (loading) {
    return (
      <Centered>
        <LoadingSpinner />
      </Centered>
    );
  }

  return (
    <DeliveryReportTable
      onPageChange={setPage}
      page={page}
      totalItems={totalItems}
      states={states}
    />
  );
}
